"use client";
/**
 * CanvasBoundary - keeps a failed React Flow chunk from taking the page down.
 *
 * The live canvas is a lazy chunk. A tab left open across a deploy asks for a
 * chunk file the new build no longer serves, the import rejects, and without a
 * boundary the error climbs to the host's nearest one and replaces the whole
 * page. Here it falls back to the static picture instead. React keeps the
 * rejected lazy import, so there is no retry: the picture stays until the page
 * is reloaded.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  fallback: ReactNode;
  children: ReactNode;
}

export class CanvasBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // A caught error never reaches window.onerror; this line is its only trace.
    console.error("[flow map] the canvas failed to load", error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
